import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs';

import { DegradedBannerComponent } from './shell/degraded-banner.component';
import { ThemeToggleComponent } from './shell/theme-toggle.component';
import { SidekickPanelComponent } from './shell/sidekick-panel.component';
import { SupportPrincipalService } from './auth/support-principal.service';
import { VarAccessService } from './auth/var-access.service';

const SIDEBAR_KEY = 'bt_support_sidebar_collapsed';
// Routes rendered WITHOUT the authed shell (no sidebar/topbar): the anonymous intake
// door and the transient auth callback.
const BARE_ROUTES = ['/intake', '/callback', '/login'];

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NgbTooltip, DegradedBannerComponent, ThemeToggleComponent, SidekickPanelComponent],
  template: `
    @if (bare()) {
      <router-outlet></router-outlet>
    } @else {
    <app-sidekick-panel></app-sidekick-panel>
    <div class="shell" [class.shell--collapsed]="collapsed()">
      <header class="topbar">
        <a class="brand" routerLink="/inbox" aria-label="BlueTitan Support">
          <img class="brand__logo brand__logo--light" src="assets/logos/Recon/Blue-Titan-Emblem-Logo-Recon@4x.png" alt="" />
          <img class="brand__logo brand__logo--dark" src="assets/logos/White/Blue-Titan-Emblem-Logo-White@4x.png" alt="" />
          <span class="brand__name">Support<span class="brand__name-accent">Console</span></span>
        </a>
        <div class="topbar__spacer"></div>
        <app-theme-toggle></app-theme-toggle>
        @if (principal.isSignedIn()) {
          <div class="whoami" data-testid="signed-in-as" [title]="principal.accountEmail() || ''">
            <span class="whoami__email">{{ principal.accountEmail() }}</span>
            <span class="whoami__avatar">{{ initials() }}</span>
          </div>
          <button type="button" class="whoami__logout" data-testid="sign-out"
                  (click)="principal.signOut()" ngbTooltip="Sign out" placement="bottom" aria-label="Sign out">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        }
      </header>

      <aside class="sidebar" aria-label="Primary">
        <button type="button" class="sidebar__collapse" (click)="toggleCollapse()"
                [attr.aria-label]="collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
                [title]="collapsed() ? 'Expand' : 'Collapse'">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline [attr.points]="collapsed() ? '9 18 15 12 9 6' : '15 18 9 12 15 6'"></polyline>
          </svg>
        </button>

        <nav class="nav">
          <!-- Inbox: staff always; a VAR only once certified (an uncertified VAR is locked
               to the certification flow, so they never see this link). -->
          @if (access.isStaff() || access.certified()) {
            <a class="nav__item" routerLink="/inbox" routerLinkActive="is-active"
               data-testid="nav-inbox" ngbTooltip="Queue inbox — every ticket you can access" placement="right">
              <svg class="nav__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 12h-6l-2 3h-4l-2-3H2"></path>
                <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
              </svg>
              <span class="nav__label">Inbox</span>
            </a>
          }
          <!-- VAR taker certification (section hub, Task 7). VARs only. -->
          @if (access.isVar()) {
            <a class="nav__item" routerLink="/certification" routerLinkActive="is-active"
               data-testid="nav-my-certification" ngbTooltip="Complete your VAR certification" placement="right">
              <svg class="nav__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="5"></circle>
                <path d="M8.5 12.5 6 22l6-3 6 3-2.5-9.5"></path>
              </svg>
              <span class="nav__label">Certification</span>
            </a>
          }
          <!-- Staff-only admin surfaces. -->
          @if (access.isStaff()) {
            <a class="nav__item" routerLink="/var-admin/principals" routerLinkActive="is-active"
               data-testid="nav-var-admin" ngbTooltip="Review VAR partner accounts and their access" placement="right">
              <svg class="nav__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
              <span class="nav__label">VAR Access</span>
            </a>
            <a class="nav__item" routerLink="/var-admin/certification" routerLinkActive="is-active"
               data-testid="nav-agent-certification" ngbTooltip="Grant, renew, or revoke VAR-agent certification" placement="right">
              <svg class="nav__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="5"></circle>
                <path d="M8.5 12.5 6 22l6-3 6 3-2.5-9.5"></path>
              </svg>
              <span class="nav__label">Certification</span>
            </a>
          }
        </nav>

        <div class="sidebar__foot">
          <span class="sidebar__foot-mark">BT</span>
          <span class="nav__label sidebar__foot-text">BlueTitan Support</span>
        </div>
      </aside>

      <main class="content">
        <app-degraded-banner></app-degraded-banner>
        <router-outlet></router-outlet>
      </main>
    </div>
    }
  `,
  styles: [`
    :host { --topbar-h: 56px; --sidebar-w: 212px; --sidebar-w-collapsed: 60px; display: block; }

    .topbar {
      position: fixed; top: 0; left: 0; right: 0; height: var(--topbar-h); z-index: 1100;
      display: flex; align-items: center; gap: 0.85rem; padding: 0 1.1rem;
      background: var(--bt-bg-surface-3); border-bottom: 1px solid var(--bt-border-light);
    }
    .brand { display: flex; align-items: center; gap: 0.6rem; text-decoration: none; }
    .brand__logo { height: 30px; width: auto; display: block; }
    .brand__logo--dark { display: none; }
    .brand__name {
      font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 18px; letter-spacing: 0.01em;
      color: var(--bt-text-primary);
    }
    .brand__name-accent { color: var(--bt-brand-intercept); margin-left: 2px; }
    .topbar__spacer { flex: 1; }

    .whoami { display: flex; align-items: center; gap: 0.6rem; }
    .whoami__email { font-size: 13px; color: var(--bt-text-secondary); max-width: 210px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .whoami__avatar {
      width: 34px; height: 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
      background: var(--bt-btn-primary-bg); color: var(--bt-btn-primary-text);
      font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em;
    }
    .whoami__logout {
      width: 34px; height: 34px; border-radius: 50%; border: none; cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      background: transparent; color: var(--bt-text-muted);
      transition: background-color .13s ease, color .13s ease;
    }
    .whoami__logout:hover { background: var(--bt-bg-surface-2); color: var(--bt-brand-intercept); }

    .sidebar {
      position: fixed; top: var(--topbar-h); bottom: 0; left: 0; width: var(--sidebar-w); z-index: 1000;
      display: flex; flex-direction: column; padding: 0.75rem 0.6rem;
      background: var(--bt-bg-surface); border-right: 1px solid var(--bt-border-light);
      transition: width .16s ease;
    }
    .shell--collapsed .sidebar { width: var(--sidebar-w-collapsed); }

    .sidebar__collapse {
      align-self: flex-end; width: 30px; height: 30px; margin-bottom: 0.5rem; border: none; border-radius: 6px;
      background: transparent; color: var(--bt-text-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    }
    .sidebar__collapse:hover { background: var(--bt-bg-surface-2); color: var(--bt-text-primary); }
    .shell--collapsed .sidebar__collapse { align-self: center; }

    .nav { display: flex; flex-direction: column; gap: 0.25rem; }
    .nav__item {
      display: flex; align-items: center; gap: 0.75rem; height: 44px; padding: 0 0.75rem; border-radius: 8px;
      color: var(--bt-text-secondary); text-decoration: none;
      font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 500;
      transition: background-color .13s ease, color .13s ease, transform .1s ease;
    }
    .nav__item:hover { background: var(--bt-bg-surface-2); color: var(--bt-text-primary); }
    .nav__item.is-active {
      background: var(--bt-btn-primary-bg); color: var(--bt-btn-primary-text); transform: translateX(3px);
    }
    .nav__icon { flex: 0 0 auto; }
    .nav__label { white-space: nowrap; overflow: hidden; }
    .shell--collapsed .nav__item { justify-content: center; padding: 0; gap: 0; transform: none; }
    .shell--collapsed .nav__label { display: none; }

    .sidebar__foot {
      margin-top: auto; display: flex; align-items: center; gap: 0.55rem; padding: 0.6rem 0.75rem;
      border-top: 1px solid var(--bt-border-light); color: var(--bt-text-muted); font-size: 12px;
    }
    .sidebar__foot-mark {
      width: 24px; height: 24px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center;
      background: var(--bt-brand-intercept); color: #fff; font-weight: 800; font-size: 11px; flex: 0 0 auto;
    }
    .shell--collapsed .sidebar__foot { justify-content: center; }
    .shell--collapsed .sidebar__foot-text { display: none; }

    .content {
      margin-top: var(--topbar-h); margin-left: var(--sidebar-w); min-height: calc(100vh - var(--topbar-h));
      background: var(--bt-bg-body); transition: margin-left .16s ease;
    }
    .shell--collapsed .content { margin-left: var(--sidebar-w-collapsed); }

    :host-context(body.theme-dark) .brand__logo--light { display: none; }
    :host-context(body.theme-dark) .brand__logo--dark { display: block; }

    @media (max-width: 720px) {
      .whoami__email { display: none; }
      .sidebar { width: var(--sidebar-w-collapsed); }
      .sidebar .nav__label, .sidebar__foot-text { display: none; }
      .sidebar .nav__item { justify-content: center; padding: 0; gap: 0; }
      .content { margin-left: var(--sidebar-w-collapsed); }
    }
  `],
})
export class AppComponent implements OnInit {
  principal = inject(SupportPrincipalService);
  access = inject(VarAccessService);
  private router = inject(Router);
  collapsed = signal<boolean>(localStorage.getItem(SIDEBAR_KEY) === 'true');
  bare = signal<boolean>(BARE_ROUTES.some((r) => this.pathname().startsWith(r)));

  constructor() {
    // Tooltip timing/placement is configured ONCE, globally, in app.config.ts
    // (NgbTooltipConfig factory: openDelay 1500, container 'body', closeDelay 0) —
    // matching ui2's canonical pattern. Do NOT re-inject and mutate NgbTooltipConfig
    // here: AppComponent constructs at bootstrap AFTER the app initializers, so any
    // value set here silently STOMPS the factory's (this is exactly how the show-delay
    // fix regressed — a stray openDelay=500 here overrode the intended 1500).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.bare.set(BARE_ROUTES.some((r) => e.urlAfterRedirects.startsWith(r))));
  }

  ngOnInit(): void {
    // Preload the VAR's certification status once so the nav can read certified()
    // synchronously (drives whether the Inbox link shows). Guarded on signed-in so a
    // stale 'partner' rail with no live session never fires an unauthenticated call.
    // Staff never call /me/certification (they 403); the guard short-circuits them.
    if (this.access.isVar() && this.principal.isSignedIn()) {
      this.access.ensureCertStatus().subscribe({ error: () => {} });
    }
  }

  private pathname(): string {
    return typeof location !== 'undefined' ? location.pathname : '/';
  }

  initials = computed(() => {
    const email = this.principal.accountEmail() ?? '';
    const name = email.split('@')[0] ?? '';
    const parts = name.split(/[.\-_]+/).filter(Boolean);
    const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
    return (chars || '?').toUpperCase();
  });

  toggleCollapse(): void {
    const next = !this.collapsed();
    this.collapsed.set(next);
    localStorage.setItem(SIDEBAR_KEY, String(next));
  }
}
